import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwh508Api, QueueItem } from '../../../services/pwh508Api';

export interface ItemState {
  item: QueueItem;
  selected: boolean;
}

export function usePWH508Queue() {
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  /** BR-F05: query the approval queue by optional FH110_01 range */
  const handleQuery = useCallback(async (startNo: string, endNo: string) => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwh508Api.queryQueue(startNo, endNo);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        // BR-V01 verbatim message text (procedures.md L_MSG9)
        message.info('無<須核准>資料!');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Toggle checkbox for a single row (Grid1 Column6 / Check1) */
  const handleToggleItem = useCallback((docNo: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) => (s.item.fh110_01 === docNo ? { ...s, selected: checked } : s))
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  /** Clear the queue after a batch approve (BR-F15: disable + refresh) */
  const resetItems = useCallback(() => {
    setItems([]);
    setQueried(false);
  }, []);

  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);

  return {
    items,
    loading,
    queried,
    selectedCount,
    allSelected,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    resetItems,
  };
}
